// ============================================================
// quota - how much one account's library may hold. Pure.
//
// 2 GB on PocketBase, 100 MB on Firebase: Firebase's free tier is 1 GiB for
// the whole project, sessions included (the user's ruling, 2026-10-18).
// Every version counts, the Trash too, until it is emptied. PocketBase also
// enforces its cap on the server (pb_hooks/assets.pb.js); Firebase cannot sum
// in a rule, so this is its only ceiling - as for handouts.
// ============================================================
import type { AssetRow } from './types';

const MIB = 1048576;
const GIB = 1073741824;

export type BackendName = 'pocketbase' | 'firebase';

/** Characters a library may hold, per backend. */
export const CAP: Record<BackendName, number> = { pocketbase: 2 * GIB, firebase: 100 * MIB };

/** Everything the library holds: every version of every asset, trashed ones included. */
export function used(assets: readonly AssetRow[], foreignBytes = 0): number {
  return assets.reduce((n, a) => n + a.bytes, foreignBytes);
}

/** Would adding `more` pass the cap? */
export function wouldExceed(
  assets: readonly AssetRow[],
  more: number,
  backend: BackendName,
  foreignBytes = 0,
): boolean {
  return used(assets, foreignBytes) + more > CAP[backend];
}

/** The sentence a refused save shows. */
export function fullSentence(backend: BackendName): string {
  return `Your library is at its ${sizeLabel(CAP[backend])}. Delete older versions or empty the Trash first.`;
}

/** A measured size: B, KB, then MB and GB to one decimal. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < MIB) return `${(n / 1024).toFixed(0)} KB`;
  if (n < GIB) return `${(n / MIB).toFixed(1)} MB`;
  return `${(n / GIB).toFixed(1)} GB`;
}

/** A limit, said the way people say it: whole MB, or whole GB from a gibibyte up. */
export function sizeLabel(n: number): string {
  return n >= GIB ? `${Math.round(n / GIB)} GB` : `${Math.round(n / MIB)} MB`;
}
